// WalletSettings.tsx - Wallet management section
import React, { useState } from 'react';
import { Button, Table, Space, Badge, message, Popconfirm, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useSuperAdmin } from '../../contexts/SuperAdminContext';
import { useEffectiveAdminId } from '../../contexts/AdminSelectorContext';
import { WalletModal } from './modals/WalletModal';

export const WalletSettings: React.FC = () => {
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    data?: any;
  }>({ open: false, mode: 'create' });

  const { wallets, deleteWallet, toggleWallet } = useSuperAdmin();
  const effectiveAdminId = useEffectiveAdminId();

  // Filter wallets based on effective admin ID
  const filteredWallets = effectiveAdminId === null
    ? wallets // SuperAdmin viewing all
    : wallets.filter(w => w.userId === effectiveAdminId);

  const handleCreate = () => {
    setModalState({ open: true, mode: 'create' });
  };

  const handleEdit = (wallet: any) => {
    setModalState({ open: true, mode: 'edit', data: wallet });
  };

  const handleDelete = (id: string) => {
    deleteWallet(id);
    message.success('Wallet deleted successfully');
  };

  const handleToggle = (wallet: any) => {
    toggleWallet({ id: wallet.id, isActive: !wallet.isActive });
    message.success(`Wallet ${wallet.name} ${!wallet.isActive ? 'activated' : 'deactivated'}`);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      key: 'type',
      render: (record: any) => {
        if (record.mpUserId) return 'MercadoPago';
        if (record.username) return 'TelePagos';
        return 'Unknown';
      },
    },
    {
      title: 'Identifier',
      key: 'identifier',
      render: (record: any) => record.mpUserId || record.username || 'N/A',
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: any) => (
        <Badge
          status={record.isActive ? 'success' : 'default'}
          text={record.isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Active',
      key: 'active',
      render: (record: any) => (
        <Switch
          checked={record.isActive}
          onChange={() => handleToggle(record)}
          size="small"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: any) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Create Wallet
        </Button>
      </div>

      <Table
        dataSource={filteredWallets}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <WalletModal
        open={modalState.open}
        mode={modalState.mode}
        initialData={modalState.data}
        onClose={() => setModalState({ open: false, mode: 'create' })}
      />
    </div>
  );
};
